import type { ElementType, FC, MouseEvent, PropsWithChildren } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ConfigType, Row, TabItem } from './declarations/type';
import {
  Badge,
  Box,
  Card,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
} from '@mui/material';
import { Filters } from './Filters';
import { TwoLineCell } from './TwoLineCell';
import { FilterPills } from './FilterPills';
import { canUnsetFilter, isFilterSet, unsetFilter } from './filterState';
import type { LabelColor } from '../Label';
import { Label } from '../Label';
import { NumberLabel } from '../NumberLabel';
import { NoDataDefaultTable } from '../table/NoDataDefaultTable';
import { Iconify } from '../Iconify';
import TableSkeleton from '../table/TableSkeleton';
import {
  ActiveFiltersBar,
  InputAdornment,
  MenuItem,
  TableMoreMenu,
  TextField,
  Typography,
} from '../../index';

import { useTranslation } from 'react-i18next';

interface PaginatedTable2Props {
  name: string;
  config: ConfigType<any, any, any>;
  rows: any[];
  isLoading: boolean;
  isError?: boolean;
  /** Renders without its own card, for a table that sits inside another card. */
  embedded?: boolean;
  /** Tightens inner cell padding; the edge cells keep the theme padding. */
  dense?: boolean;
}

interface TabComponentProps {
  tab: TabItem<unknown>;
}

const TabComponent: FC<TabComponentProps> = ({ tab }) => {
  const initialTabCount = typeof tab.count === 'number' ? tab.count : undefined;
  const [count, setCount] = useState(initialTabCount);

  useEffect(() => {
    if (tab.count && typeof tab.count !== 'number') {
      tab.count.then((count) => setCount(count));
    } else if (typeof tab.count === 'number') {
      setCount(tab.count);
    }
  }, [tab.count]);

  if (count === undefined) {
    return (
      <Stack
        spacing={1}
        direction="row"
        sx={{
          alignItems: 'center',
        }}
      >
        <Label
          variant={tab.variant ?? 'ghost'}
          color={tab.color as LabelColor}
          sx={{ cursor: 'pointer' }}
        >
          {tab.label}
        </Label>
      </Stack>
    );
  }

  return (
    <Stack
      spacing={1}
      direction="row"
      sx={{
        alignItems: 'center',
      }}
    >
      <div>{tab.label}</div>
      <NumberLabel color={tab.color} variant="ghost">
        {count}
      </NumberLabel>
    </Stack>
  );
};

interface TabsComponentProps {
  tabs: ConfigType<any, any, any>['tabs'];
  onChange: (value: any) => void;
}

const TabsComponent: FC<TabsComponentProps> = ({ tabs, onChange }) => {
  return (
    <>
      <Tabs
        allowScrollButtonsMobile
        variant="scrollable"
        scrollButtons="auto"
        value={tabs?.selectedItem ?? null}
        onChange={(event, value: any) => onChange(value)}
        sx={{
          px: 2,
          '&:has(> .MuiTabs-scrollButtons)': {
            px: 0,
          },
          '& .MuiTabScrollButton-root.Mui-disabled': {
            opacity: 0.3,
          },
        }}
      >
        {tabs?.items?.map((tab) => (
          <Tab
            data-cy={`tab-${tab.value}`}
            disableRipple
            key={tab.value as unknown as string}
            value={tab.value}
            label={<TabComponent tab={tab} />}
            sx={
              tab.separatorAfter
                ? { borderRight: 1, borderColor: 'divider', pr: 2 }
                : undefined
            }
          />
        ))}
      </Tabs>
      <Divider />
    </>
  );
};

// Fills the header row's left side when a table has filters but no tabs.
const TableHeaderTitle: FC<{ title?: string }> = ({ title }) => {
  const { t } = useTranslation();
  if (title) {
    return (
      <Typography variant="subtitle1" sx={{ px: 3, py: 1.5 }}>
        {title}
      </Typography>
    );
  }
  return (
    <Tabs value="all" sx={{ px: 2 }}>
      <Tab disableRipple value="all" label={t('common.all')} />
    </Tabs>
  );
};

export const PaginatedTable2: FC<PaginatedTable2Props> = ({
  name,
  config,
  rows,
  isLoading,
  isError,
  embedded = false,
  dense = false,
}) => {
  const { t } = useTranslation();
  const Wrapper: ElementType = embedded ? Box : Card;

  const pills = config.filters?.variant === 'pills' ? config.filters : null;
  const highlighted = pills?.items.filter((filter) => filter.highlighted) ?? [];
  const collapsible =
    pills?.items.filter((filter) => !filter.highlighted) ?? [];
  // Open on first render when a deep link already set one of the hidden ones.
  const [filterRowOpen, setFilterRowOpen] = useState(() =>
    collapsible.some(isFilterSet)
  );
  // Only what the collapsed row hides: a set highlighted pill speaks for itself.
  const hiddenActive = collapsible.filter(isFilterSet).length;
  // MUI hides a numeric 0 but would render "+0", so keep the number for none.
  const badgeContent = hiddenActive > 0 ? `+${hiddenActive}` : 0;
  const clearable =
    pills?.items.filter(
      (filter) => isFilterSet(filter) && canUnsetFilter(filter)
    ) ?? [];
  // URL-backed filters batch their writes, so one unset per filter is one update.
  const clearAll = () => clearable.forEach(unsetFilter);
  const resetPage = () => config?.pagination?.onPageChange(0);
  const isFiltered = config.filters?.items.some(isFilterSet) ?? false;

  // A select whose value fell out of its options, typically after a scope
  // switch, would silently empty the table next to a blank picker. Waits for
  // the options so a deep link survives loading.
  const filterItems = config.filters?.items;
  useEffect(() => {
    filterItems?.forEach((filter) => {
      if (
        filter.type === 'select' &&
        filter.value &&
        filter.options.length > 0 &&
        !filter.options.some((option) => option.id === filter.value)
      ) {
        filter.onChange(undefined);
      }
    });
  }, [filterItems]);

  return (
    <Wrapper>
      {(config.tabs || config.filters) && (
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'background.neutral',
          }}
        >
          {config.tabs ? (
            <TabsComponent
              tabs={config.tabs}
              onChange={(value) => {
                config?.tabs?.updateFilterModel(value);
                resetPage();
              }}
            />
          ) : (
            <TableHeaderTitle title={config.title} />
          )}
          {config.filters && !pills && (
            <Filters filters={config.filters} onChange={resetPage} />
          )}
          {pills && (
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, pr: 2 }}>
              <FilterPills items={highlighted} onChange={resetPage} />
              {collapsible.length > 0 && (
                <IconButton
                  data-testid={`${name}-filter-toggle`}
                  aria-label={t('common.filter')}
                  aria-expanded={filterRowOpen}
                  color={filterRowOpen ? 'primary' : 'default'}
                  onClick={() => setFilterRowOpen((open) => !open)}
                >
                  <Badge
                    badgeContent={badgeContent}
                    color="secondary"
                    max={99}
                    sx={{ '& .MuiBadge-badge': { top: 5 } }}
                  >
                    <Iconify
                      icon={
                        hiddenActive ? 'eva:funnel-fill' : 'eva:funnel-outline'
                      }
                      width={22}
                    />
                  </Badge>
                </IconButton>
              )}
            </Stack>
          )}
        </Stack>
      )}
      {(config.tabs || config.filters) && <Divider />}

      {config.toolbar && <TableToolbar config={config} />}

      {pills && filterRowOpen && collapsible.length > 0 && (
        <FilterPills
          items={collapsible}
          onChange={resetPage}
          clearAll={clearable.length > 0 ? clearAll : undefined}
          sx={{ px: 3, pt: 2, pb: 1.5 }}
        />
      )}

      {config.filters && config.filters.showActiveFiltersBar && (
        <Stack
          sx={{
            pt: 3,
            pb: 1,
          }}
        >
          <ActiveFiltersBar
            filters={config.filters}
            noFiltersLabel={
              config.filters.noActiveFiltersLabel ??
              t('common.table-no-filter-set')
            }
          />
        </Stack>
      )}
      <TableContainer>
        <Table
          sx={
            dense
              ? {
                  '& .MuiTableCell-root:not(:first-of-type)': { pl: 1 },
                  '& .MuiTableCell-root:not(:last-of-type)': { pr: 1 },
                }
              : undefined
          }
        >
          <TableHeadComponent config={config} rows={rows} />
          <TableBodyComponent
            config={config}
            rows={rows}
            isLoading={isLoading}
            isError={isError}
            isFiltered={isFiltered}
          />
        </Table>
        {config.pagination && (
          <TablePagination
            rowsPerPageOptions={config.pagination.pageSizeOptions}
            count={config.pagination.total}
            rowsPerPage={config.pagination.pageSize}
            page={config.pagination.page}
            onPageChange={(_e, newPage) =>
              config?.pagination?.onPageChange(newPage)
            }
            onRowsPerPageChange={(event) => {
              config?.pagination?.onPageSizeChange(
                parseInt(event.target.value, 10)
              );
              config?.pagination?.onPageChange(0);
            }}
            slotProps={{ root: { component: 'div' } }}
            data-testid={`${name}-pagination`}
          />
        )}
      </TableContainer>
    </Wrapper>
  );
};

interface TableHeadComponentProps {
  config: ConfigType<Row<unknown>, string, string>;
  rows: Array<Row<unknown>>;
}

function ColumnLabel({
  column,
}: {
  column: { label: string; subLabel?: string; align?: string };
}) {
  // Replace space with non breaking space
  const label = column.label.replace(' ', '\u00a0');
  if (!column.subLabel) {
    return label;
  }
  return (
    <TwoLineCell
      primary={label}
      secondary={column.subLabel.replace(' ', '\u00a0')}
      align={column.align === 'right' ? 'right' : 'left'}
    />
  );
}

function TableHeadComponent({ config, rows }: TableHeadComponentProps) {
  const selection = config.selection;
  const allSelected = selection
    ? rows.length > 0 && rows.every((row) => selection.selectedIds.has(row.id))
    : false;
  const someSelected = selection
    ? rows.some((row) => selection.selectedIds.has(row.id)) && !allSelected
    : false;

  return (
    <TableHead>
      <TableRow>
        {selection && (
          // MUI's checkbox padding adds 4px on the left on top of the
          // Checkbox's own 9px, which reads as an off-centre first column.
          <TableCell padding="checkbox" sx={{ pl: 0 }}>
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={() => selection.onToggleAll(rows.map((r) => r.id))}
            />
          </TableCell>
        )}
        {config.columns.map((column) => {
          const isActive = config.sortColumn?.column === column.key;
          const direction =
            config.sortColumn?.direction === 'ASC' ? 'asc' : 'desc';

          return (
            <TableCell key={column.key} align={column.align} sx={column.sx}>
              {column.onSort ? (
                <TableSortLabel
                  active={isActive}
                  direction={isActive ? direction : 'asc'}
                  onClick={() =>
                    column.onSort!(
                      isActive && direction === 'asc' ? 'DESC' : 'ASC'
                    )
                  }
                >
                  <ColumnLabel column={column} />
                </TableSortLabel>
              ) : (
                <ColumnLabel column={column} />
              )}
            </TableCell>
          );
        })}
        {config.options && <TableCell />}
      </TableRow>
    </TableHead>
  );
}

interface TableBodyComponentProps {
  config: ConfigType<any, any, any>;
  rows: any[];
  isLoading: boolean;
  isError?: boolean;
  isFiltered: boolean;
}

function TableBodyComponent({
  isLoading,
  isError,
  isFiltered,
  ...props
}: TableBodyComponentProps) {
  const rowsPerPage = props.config.pagination?.pageSize ?? 5;
  let colSpan = props.config.columns.length;
  if (props.config.options) {
    colSpan += 1;
  }
  if (props.config.selection) {
    colSpan += 1;
  }
  const rows = props.rows;
  const emptyRows = props.config.hideEmptyRows ? 0 : rowsPerPage - rows.length;
  const emptyRowHeight = props.config.emptyRowHeight ?? 60;

  if (isLoading) {
    return (
      <TableBody>
        {Array.from(Array(rowsPerPage).keys()).map((row: number) => (
          <TableSkeleton key={`row- ${row}`} colSpan={colSpan} />
        ))}
      </TableBody>
    );
  }

  if (rows.length === 0) {
    return (
      <TableBody>
        <NoDataDefaultTable
          isNoData
          dataAvailableBeforeFilter
          isFiltered={isFiltered}
          isError={isError}
          colSpan={colSpan}
        />
      </TableBody>
    );
  }

  return (
    <TableBody>
      {rows.map((row, index) => (
        <TableRowComponent key={row.id} config={props.config} row={row} />
      ))}
      {emptyRows > 0 && (
        <TableRow style={{ height: emptyRowHeight * emptyRows }}>
          <TableCell colSpan={colSpan} />
        </TableRow>
      )}
    </TableBody>
  );
}

interface TableRowMenuProps<T extends Row<unknown>> {
  options: ConfigType<T, any, any>['options'];
  row: T;
}

function TableRowMenu<T extends Row<unknown>>(props: TableRowMenuProps<T>) {
  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenMenuActions(event.currentTarget);
    event.preventDefault();
    event.stopPropagation();
  };

  const handleCloseMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenMenuActions(null);
    event.preventDefault();
    event.stopPropagation();
  };

  const items = useMemo(() => {
    if (!props.options) {
      return [];
    }

    let items = [];
    if (typeof props.options.items === 'function') {
      items = props.options.items(props.row);
    } else {
      items = props.options.items;
    }

    return items.map((item) => {
      return {
        ...item,
        onClick: async (event: MouseEvent<HTMLElement>) => {
          event.preventDefault();
          event.stopPropagation();
          const result = await item.onClick(props.row);
          handleCloseMenu(event);
          return result;
        },
      };
    });
  }, [props.options, props.row]);

  if (props.options === undefined) {
    return null;
  }

  if (items.length === 0) {
    return <TableCell align="right"></TableCell>;
  }

  return (
    <TableCell align="right">
      <TableMoreMenu
        open={openMenu}
        onOpen={handleOpenMenu}
        onClose={handleCloseMenu}
        actions={
          <>
            {items.map((item) => {
              return (
                <LoadingMenuItem
                  open={Boolean(openMenu)}
                  key={item.key ?? item.label}
                  onClick={item.onClick}
                  errorVariant={item.errorVariant}
                >
                  {item.icon}
                  {item.label}
                </LoadingMenuItem>
              );
            })}
          </>
        }
      />
    </TableCell>
  );
}

interface LoadingMenuItemProps {
  onClick: (event: MouseEvent<HTMLElement>) => Promise<void>;
  open: boolean;
  errorVariant?: boolean;
}

const LoadingMenuItem: FC<PropsWithChildren<LoadingMenuItemProps>> = ({
  onClick: onClickOriginal,
  open,
  errorVariant,
  children,
}) => {
  const [loading, setLoading] = useState(false);
  const onClick = useCallback(
    async (event: MouseEvent<HTMLElement>) => {
      setLoading(true);
      await onClickOriginal(event);
    },
    [onClickOriginal]
  );
  return (
    <MenuItem
      onClick={onClick}
      disabled={loading}
      sx={{
        color: errorVariant ? 'error.main' : undefined,
      }}
    >
      {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
      {!loading && children}
    </MenuItem>
  );
};

interface TableRowComponentProps {
  config: ConfigType<any, any, any>;
  row: any;
}

function TableRowComponent(props: TableRowComponentProps) {
  const { onClickRow, hoverRow } = props.config;
  const onClick = useCallback(() => {
    onClickRow?.(props.row);
  }, [onClickRow, props.row]);

  const rowCursorStyle = useMemo(() => {
    if (onClickRow) {
      return 'pointer';
    } else {
      if (hoverRow) {
        return 'text';
      } else {
        return undefined;
      }
    }
  }, [hoverRow, onClickRow]);

  return (
    <TableRow
      hover={hoverRow ?? Boolean(onClickRow)}
      onClick={onClickRow && onClick}
      sx={rowCursorStyle ? { cursor: rowCursorStyle } : {}}
      selected={props.config.selection?.selectedIds.has(props.row.id)}
    >
      {props.config.selection && (
        <TableCell
          padding="checkbox"
          sx={{ pl: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={props.config.selection.selectedIds.has(props.row.id)}
            onChange={() => props.config.selection?.onToggleRow(props.row.id)}
          />
        </TableCell>
      )}
      {props.config.columns.map((column) => {
        return (
          <TableCell
            key={column.key}
            align={column.align}
            sx={{
              minWidth: column.minWidth,
              maxWidth: column.maxWidth,
            }}
          >
            <column.component row={props.row} />
          </TableCell>
        );
      })}
      <TableRowMenu options={props.config.options} row={props.row} />
    </TableRow>
  );
}

interface TableToolbarProps {
  config: ConfigType<any, any, any>;
}

export const TableToolbar: FC<TableToolbarProps> = ({ config }) => {
  if (!config.toolbar?.searchBar) {
    return null;
  }

  const value = config.toolbar.searchBar.query;
  const onChangeValue = (value: string) => {
    config.pagination?.onPageChange(0);
    config.toolbar?.searchBar?.onQueryChange(value);
  };

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        gap: 1,
        py: 2.5,
        px: 3,
      }}
    >
      {config.toolbar.primarySelection &&
        config.toolbar.primarySelection.component({
          onChange: (value: any) => {
            config.pagination?.onPageChange(0);
            config.toolbar?.primarySelection?.onChange(value);
          },
        })}
      <TextField
        fullWidth
        value={value}
        onChange={(event) => onChangeValue(event.target.value)}
        placeholder={config.toolbar.searchBar.label}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Iconify
                  icon={'eva:search-fill'}
                  sx={{
                    color: 'text.disabled',
                    width: 20,
                    height: 20,
                  }}
                />
              </InputAdornment>
            ),
          },
        }}
      />
    </Stack>
  );
};
